/**
 * Bridge for src-tauri Chronicle online session IPC commands.
 *
 * This consolidates all `chronicle_session_*` Tauri commands into a single
 * module. The session layer in Rust orchestrates both the upload pipeline
 * AND device communication (start/stop recording) — the frontend does NOT
 * need to call `chronicleBridge` (plugin commands) directly for recording
 * lifecycle.
 *
 * - `chronicleBridge`          → BLE / device state queries (plugin IPC)
 * - `chronicleSessionBridge`   → session lifecycle + upload pipeline (src-tauri IPC)
 */
import { isTauri } from '@/tauri/utils';

// ---------------------------------------------------------------------------
// Types (mirror the Rust structs in src-tauri/src/ipc/chronicle_session.rs)
// ---------------------------------------------------------------------------

export interface StartSessionParams {
  sessionId: string;
  recordingId: string;
  baseUrl: string;
  token: string;
  userId?: string;
  /**
   * APP-3779: Whether this session uses the v2 EKS transcription service for
   * chunk uploads (`PUT /recordings/{id}` + `POST /transcribe-chunk`,
   * `:finish` skipped). When false, Rust uses the existing ml-scribe
   * `:transcribe` / `:finish` endpoints. Locked for the lifetime of the
   * session — the upload worker reads this value, not the live flag.
   */
  useTranscriptionServiceV2: boolean;
  /**
   * APP-3779: EKS transcription service base URL (e.g.
   * `https://api.aws-aue1.heidihealth.com/transcription/main/v1`). Required
   * when `useTranscriptionServiceV2` is true; ignored otherwise. Resolved
   * from `getTranscriptionBaseUrl()` on the TS side — i.e. EKS's internal
   * `/v1` API path. Matches Android `TRANSCRIPTION_V2_BASE = "transcription/main/v1"`.
   * EKS's `/v2` internal API is a separate, unrelated feature used by web
   * live recording (shipped 100%). Pass `null` for v1 (ml-scribe).
   */
  transcriptionBaseUrl: string | null;
  /**
   * APP-3779: BCP-47 language codes to include in the v2 register call body.
   * Empty array is acceptable (EKS accepts an empty `languages` array).
   * Ignored when `useTranscriptionServiceV2` is false.
   */
  languages: string[];
  /**
   * When true, Rust force-refreshes Auth0 on chunk-upload 401. Locked at session start.
   */
  rustAuth0TokenRefresh: boolean;
  /** Start path, carried onto the Datadog `scribe.remote.*` breadcrumbs. */
  trigger?: 'app_start' | 'app_resume' | 'device_start';
}

export interface StopSessionResult {
  totalBytes: number;
  totalChunks: number;
  durationMs: number;
  /**
   * True when Rust routed a dirty stop (the server did not get every chunk)
   * down the keep-the-device-file bulk-sync recovery path. The FE masks the
   * stale online note until the sweep re-uploads. Always false unless the
   * dirty-stop-recovery flag is on.
   */
  recoveryPending: boolean;
}

export interface ChronicleSessionState {
  sessionId: string | null;
  recordingId: string | null;
  isActive: boolean;
  bytesUploaded: number;
  chunksUploaded: number;
  lastError: string | null;
}

// ---------------------------------------------------------------------------
// Internal invoke helper
// ---------------------------------------------------------------------------

class ChronicleSessionBridgeError extends Error {
  /** Server-supplied error code, present when the Rust IPC handler returned an error. */
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'ChronicleSessionBridgeError';
    this.errorCode = errorCode;
  }
}

/** Shape Rust returns via `Ok(IpcResponse::from(...))`. See `ipc/response.rs:22`. */
type IpcResponseEnvelope<D> = {
  result?: { data: D } | null;
  error?: { message: string; error_code?: string } | null;
};

/**
 * Invokes a Tauri chronicle-session command and unwraps the `IpcResponse`
 * envelope returned by the Rust handler (`Ok(IpcResponse::from(result))`).
 *
 * Behaviour mirrors `src/tauri/ipc.ts:ipc_invoke`:
 * - `response.error != null` → throw `ChronicleSessionBridgeError` with the
 *   server-supplied `error_code` attached. Callers can map known codes
 *   (e.g. `V2_MISSING_TRANSCRIPTION_BASE_URL`) to user-facing messages.
 * - `response.result == null` → throw (malformed response).
 * - otherwise return `response.result.data`.
 *
 * Unlike `ipc_invoke`, no toast is shown here — chronicle bridge callers
 * (e.g. `useChronicleStartRecording`) already render their own toast in
 * the catch block, so we'd double-toast on failure.
 */
async function invokeSessionCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new ChronicleSessionBridgeError(
      'Chronicle session bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  const response = await invoke<IpcResponseEnvelope<T>>(command, payload ?? {});

  if (response?.error != null) {
    throw new ChronicleSessionBridgeError(
      response.error.message,
      response.error.error_code
    );
  }
  if (response?.result == null) {
    throw new ChronicleSessionBridgeError(
      `chronicle bridge: ${command} returned malformed IpcResponse`
    );
  }
  return response.result.data;
}

// ---------------------------------------------------------------------------
// Public bridge
// ---------------------------------------------------------------------------

export const chronicleSessionBridge = {
  /**
   * Start a Chronicle online session.
   *
   * Configures the upload pipeline (worker, backup, auth), then starts
   * recording on the Chronicle device. For device-initiated starts where
   * the device is already recording, the device call is idempotent — it
   * associates the server sessionId and wires up the streaming pipeline.
   *
   * Recording filename for post-session file deletion is auto-captured
   * by a Rust state watcher — no FE involvement needed.
   *
   * Must be called before the first chunk upload deadline (~45 s).
   */
  async startSession(params: StartSessionParams): Promise<void> {
    return invokeSessionCommand<void>('chronicle_session_start', {
      params,
    });
  },

  /**
   * Refresh the auth token for the upload pipeline.
   *
   * Called by the frontend in response to a `chronicle:token_refresh_needed`
   * event (when the upload pipeline gets a 401 from the server).
   */
  async refreshToken(token: string): Promise<void> {
    return invokeSessionCommand<void>('chronicle_session_refresh_token', {
      params: { token },
    });
  },

  /**
   * Return the current session/upload pipeline state.
   *
   * Includes bytes uploaded, chunks uploaded, and any error information.
   */
  async getSessionState(): Promise<ChronicleSessionState> {
    return invokeSessionCommand<ChronicleSessionState>('chronicle_session_get_state');
  },

  /**
   * Stop a Chronicle online session.
   *
   * Stops recording on the Chronicle device (idempotent if device-initiated),
   * flushes remaining audio, awaits the upload worker drain, uploads the
   * final chunk, calls `:finish`, uploads the backup file, deletes the
   * recording file from the device (best-effort), cleans up the
   * `sync_tracking` record (non-interrupted only), and emits
   * `chronicle:stream_finished`.
   *
   * **Blocking**: awaits until ALL uploads (queued + final + backup) complete
   * and `:finish` is called. The frontend can safely trigger note generation
   * after this call resolves.
   *
   * @param opts.isInterrupted - When true (BLE disconnect), the sync_tracking
   *   record is preserved for bulk sync live recovery.
   * @param opts.preserveDeviceFileForRecovery - when true, the
   *   on-device recording file + sync_tracking row are kept for bulk-sync
   *   recovery WITHOUT the interrupted-UI / pending-session side-effects of
   *   `isInterrupted`. Set on a live-recovery end, where the on-device file is
   *   the only complete copy (the recovered recording never streamed live).
   * @param opts.recoverOnDirtyStop - when true, a stop whose upload pipeline is
   *   dirty (the server did not get every chunk) keeps the on-device file +
   *   sync_tracking row so the bulk sweep re-uploads the full recording, and the
   *   result reports `recoveryPending`. Flag-gated; false is the unchanged path.
   * @param opts.userId - Required for sync_tracking cleanup (composite PK).
   */
  async stopSession(opts?: {
    isInterrupted?: boolean;
    preserveDeviceFileForRecovery?: boolean;
    recoverOnDirtyStop?: boolean;
    userId?: string;
    /** Bound session id — evidence for the recovered-stop path (APP-7870). */
    sessionId?: string;
  }): Promise<StopSessionResult> {
    return invokeSessionCommand<StopSessionResult>('chronicle_session_stop', {
      isInterrupted: opts?.isInterrupted ?? false,
      preserveDeviceFileForRecovery: opts?.preserveDeviceFileForRecovery ?? false,
      recoverOnDirtyStop: opts?.recoverOnDirtyStop ?? false,
      userId: opts?.userId,
      sessionId: opts?.sessionId,
    });
  },
};

export { ChronicleSessionBridgeError };
